<template>
    <div
        :style="cssVars"
        class="redesign-table-component"
        data-testid="data-table-component"
        :class="{
            'is-rtl': isRTLMode,
            'table-wrap-text': isWrapText,
            'fix-first-column': fixFirstColumn
        }"
    >
        <Portal :to="`widget-actions__${widgetData.WidgetID}`">
            <div
                class="flex items-center justify-between widget-footer"
                data-testid="data-table-footer"
            >
                <div
                    class="flex p-2 cursor-pointer"
                    data-testid="data-table-export-button"
                    @click="openExportDataModal"
                >
                    <VcIcon
                        icon="download"
                        :size="18"
                        class="text-primary"
                    />
                    <span class="mx-1 hover:text-primary">
                        {{ t('widget.export.export') }}
                    </span>
                </div>
            </div>
        </Portal>
        <Portal :to="`widget-header__${widgetData.WidgetID}-action`">
            <div
                v-show="withColumnsManage"
                data-testid="data-table-column-manage-container"
            >
                <VcButton
                    type="outline"
                    icon="settings-2"
                    class="mt-2 mb-2"
                    data-test-name="data-table-manage-columns-button"
                    @click="isManageSideModalOpen = true"
                >
                    {{ t('datatable.manage.columns') }}
                </VcButton>
            </div>
        </Portal>

        <RedesignColumnsManageModal
            :visible="isManageSideModalOpen"
            :has-unsaved-changes="hasManageColumnsUnsavedChanges"
            :visible-columns="sortedColumns"
            :hidden-columns="columnByVisibility.hidden"
            :columns-config="localColumnsConfig"
            :global-headers-config="tableHeadersConfig"
            @change-label="changeLabelType"
            @change-icons-visibility="changeIconVisibility"
            @update-columns-config="updateColumnsConfig"
            @regenerate-columns-config="generateColumnsConfigAutomatically"
            @cancel-manage="cancelManage"
            @apply-changes="onApply"
        />

        <RedesignExportData
            ref="exportDataModalRef"
            :widget-title="widgetData.Title"
            :table-data="tableDataForExport"
            :widget="widgetData"
        />

        <ElTable
            v-if="sortedColumns"
            :ref="onRefDefined"
            :data="filteredTableData"
            :row-key="rowKey"
            :stripe="stripe"
            :border="border"
            :default-sort="sortingParams"
            :max-height="tableHeight"
            data-testid="data-table-element"
            @header-dragend="resizeColumn"
            @sort-change="onSortChange"
        >
            <ElTableColumn
                v-for="(column, i) in sortedColumns"
                :key="column.prop + i"
                :fixed="i === 0 && fixFirstColumn && !isRTLMode ? 'left' : false"
                :prop="column.prop"
                :align="getColumnAlignment(column.prop)"
                sortable
                :resizable="withColumnsManage && !isRTLMode"
                :width="getColumnWidth(column.prop)"
            >
                <template #header>
                    <div
                        :class="withColumnsManage ? 'with-columns-manage' : ''"
                        class="w-full flex items-center"
                        :data-prop="column.prop"
                    >
                        <RedesignTableHeader
                            :column="column"
                            :global-headers-config="tableHeadersConfig"
                            :column-config="(localColumnsConfig[column.prop] ?? columnsConfig[column.prop])"
                            :label-text-type="tableHeadersConfig.labelTextType"
                            :show-all-icons="tableHeadersConfig.showAllIcons"
                            :font-size="dynamicHeaderFontSize"
                            :with-manage-columns="withColumnsManage"
                            :current-filter-values="columnFilters[column.prop] || []"
                            @update-column-config="updateColumnConfig"
                            @update-global-headers-config="updateGlobalHeadersConfig"
                            @filter-change="handleColumnFilterChange"
                        />
                    </div>
                </template>
                <template #default="{ row }">
                    <slot
                        name="cell"
                        :row="row"
                        :column="column"
                        :font-size="dynamicFontSize"
                    >
                        <span v-html="row[column.prop]" />
                    </slot>
                </template>
            </ElTableColumn>
        </ElTable>
    </div>
</template>

<script setup lang="ts">
import { ElTable, ElTableColumn } from 'element-plus'
import RedesignExportData from '@/components/RedesignComponents/RedesignTableComponents/RedesignExportData.vue'
import RedesignTableHeader from '@/components/RedesignComponents/RedesignTableComponents/RedesignTableHeader.vue'
import RedesignColumnsManageModal from '@/components/RedesignComponents/RedesignTableComponents/RedesignColumnsManageModal.vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTableFilters } from '@/composables/useTableFilters'
import { useTableColumnUpdater } from '@/composables/useTableColumnsUpdater'
import cloneDeep from 'lodash/cloneDeep.js'
import isEqual from 'lodash/isEqual.js'
import { widgetTableContentFontSize, widgetTableHeaderFontSize } from '@/composables/globalData/useLayouts'
import { isPreviewLayout, widgetPreviewTableHeaderFontSize, widgetPreviewTableContentFontSize } from '@/composables/usePreviewLayout'
import { useColumnsStatusManage } from '@/composables/useColumnsStatusManage'
import { getTableHeaderGlobalOptions } from '@/enum/redesignTableHeaderOptions'
import { DEFAULT_COLUMN_WIDTH, defaultColumnConfig } from '@/enum/redesignTableConfig'
import { isRTL } from '@/composables/useDirection'
import type { TableAlignmentType } from '@/types/alignment'
import { TableAlignment } from '@/types/alignment'
import { invertAlignmentForRTL } from '@/helpers/alignmentUtils'
import {
    ColumnConfig,
    ColumnsObjectConfig,
    TableHeadersConfig,
    WidgetExtended,
    WidgetTemplateColumnWithConfig
} from '@/types/dashboard'
import { VcButton, VcIcon } from '@voicenter-team/voicenter-ui-plus'


type ColumnConfigType = Record<string, ColumnConfig>
type RowKeyType = string | ((row: Record<string, unknown>) => string | number)

const { t } = useI18n()

const props = withDefaults(
    defineProps<{
        columns?: WidgetTemplateColumnWithConfig[]
        tableData: Record<string, string | number>[]
        notPaginatedTableData?: Record<string, string | number>[] | undefined
        stripe?: boolean
        border?: boolean
        paginated?: boolean
        paginationHeight?: number
        withColumnsManage?: boolean
        columnsConfig: ColumnConfigType
        widgetData: WidgetExtended
        defaultAvailableColumns?: Record<string, boolean> | undefined
        fixFirstColumn?: boolean
        rowKey?: RowKeyType
    }>(),
    {
        tableData: () => [],
        notPaginatedTableData: undefined,
        columns: () => ([]),
        defaultAvailableColumns: undefined,
        stripe: false,
        paginationHeight: 48,
        border: true,
        withColumnsManage: true,
        paginated: false,
        fixFirstColumn: false,
        rowKey: undefined
    }
)

const { setInColumnsStatusValue } = useColumnsStatusManage()
const localColumnsConfig = ref<ColumnConfigType>({})
const manageColumnsInitialState = ref<{
    columnsConfig: ColumnConfigType
    tableHeadersConfig: TableHeadersConfig
} | null>(null)
const isManageSideModalOpen = ref(false)
const isMounted = ref(false)
const tableHeight = ref(300)

const exportDataModalRef = ref<typeof RedesignExportData>()

// RTL mode detection
const isRTLMode = computed(() => isRTL.value)

// Table component ref
const tableRef = ref<InstanceType<typeof ElTable> | null>(null)

// Table element ref for RTL resize
const tableElementRef = ref<HTMLElement | null>(null)

// Get table header options with translated labels at runtime
const tableHeaderGlobalOptions = computed(() => getTableHeaderGlobalOptions())

const tableHeadersConfig = ref({
    showAllIcons: false,
    labelTextType: tableHeaderGlobalOptions.value.fullText.label
})

const hasManageColumnsUnsavedChanges = computed(() => {
    if (!manageColumnsInitialState.value) return false
    return !isEqual(localColumnsConfig.value, manageColumnsInitialState.value.columnsConfig)
        || !isEqual(tableHeadersConfig.value, manageColumnsInitialState.value.tableHeadersConfig)
})

const { getColumnsConfig, onRefDefined: originalOnRefDefined, cleanUp } = useTableColumnUpdater((prop, newWidth) => {
    updateColumnConfig(prop, {
        ...localColumnsConfig.value[prop],
        width: newWidth ?? 100
    })
})

// Wrap onRefDefined to store table element for RTL resize
const onRefDefined = (ref: InstanceType<typeof ElTable>) => {
    originalOnRefDefined(ref)
    tableRef.value = ref
    if (ref?.$el) {
        tableElementRef.value = ref.$el as HTMLElement
        setupRTLResizeListeners()
    }
}

const emit = defineEmits<{
    (event: 'update-widget', widgetData: WidgetExtended): void;
    (event: 'update-columns-config', config: ColumnsObjectConfig): void
    (event: 'update-headers-and-columns-config', config: ColumnsObjectConfig, tableHeadersConfig: TableHeadersConfig): void
}>()

//Computed
const tableDataForExport = computed(() => {
    return props.notPaginatedTableData ?? props.tableData
})
const columnFilters = computed(() =>
    Object.fromEntries(
        Object.entries(localColumnsConfig.value)
            .filter(([ , config ]) => config?.filterValues?.length)
            .map(([ columnProp, config ]) => [ columnProp, config.filterValues! ])
    )
)
// Composable for table filtering
const { filteredTableData } = useTableFilters(
    toRef(props, 'tableData'),
    toRef(props, 'columns'),
    columnFilters
)
const columnByVisibility = computed(() => {
    if (!isColumnsConfigAdded.value) {
        return {
            hidden: [],
            visible: props.columns
        }
    }

    return props.columns.reduce<{
        hidden: Array<WidgetTemplateColumnWithConfig>,
        visible: Array<WidgetTemplateColumnWithConfig>
    }>(
        (acc, column) => {
            if (localColumnsConfig.value[column.prop]?.hidden) {
                acc.hidden.push(column)
            } else {
                acc.visible.push(column)
            }

            return acc
        },
        {
            hidden: [],
            visible: []
        }
    )
})

//the array of columns that was configured on table component before redesign
const oldVersionVisibleColumns = computed(() => {
    return props.widgetData.WidgetLayout?.Columns?.visibleColumns ?? []
})

const sortedColumns = computed(() => {
    if (!isColumnsConfigAdded.value) {
        return props.columns
    }
    return [ ...columnByVisibility.value.visible ].sort((a, b) => {
        const orderA = localColumnsConfig.value[a.prop]?.order
        const orderB = localColumnsConfig.value[b.prop]?.order
        return orderA - orderB
    })
})

const getColumnAlignment = (columnProp: string): TableAlignmentType => {
    let alignment: TableAlignmentType

    if (localColumnsConfig.value[columnProp]?.align) {
        alignment = localColumnsConfig.value[columnProp].align
    } else {
        alignment = props.widgetData?.WidgetLayout?.tableColumnsAlignment || TableAlignment.left
    }

    return invertAlignmentForRTL(alignment)
}

const isColumnsConfigAdded = computed(() => {
    return Object.values(props.columnsConfig).length
})

const layoutFontSize = computed(() => {
    if (isPreviewLayout.value) {
        return widgetPreviewTableContentFontSize.value.fontSize
    }
    return widgetTableContentFontSize.value.fontSize
})
const tableHeaderFontSize = computed(() => {
    if (isPreviewLayout.value) {
        return widgetPreviewTableHeaderFontSize.value.fontSize
    }
    return widgetTableHeaderFontSize.value.fontSize
})

const sortingParams = computed(() => {
    return props.widgetData.WidgetLayout?.sortingParams || {}
})

const dynamicFontSize = computed(() => {
    return props.widgetData.WidgetLayout?.tableContentFontSize ?? layoutFontSize.value
})
const dynamicHeaderFontSize = computed(() => {
    return props.widgetData.WidgetLayout?.tableHeaderFontSize ?? tableHeaderFontSize.value
})

const isWrapText = computed(() => {
    return props.widgetData.WidgetLayout?.isWrap ?? false
})

const cssVars = computed(() => {
    return {
        '--dynamic-font-size': dynamicFontSize.value,
        '--table-cell-vertical-padding': `${props.widgetData.WidgetLayout?.tableRowPadding ?? 12}px`,
    }
})
//Methods
const emitColumnsConfigUpdate = () => {
    emit('update-columns-config', localColumnsConfig.value)
}

const emitColumnsAndHeadersConfigUpdate = () => {
    emit('update-headers-and-columns-config', localColumnsConfig.value, tableHeadersConfig.value)
}

const getColumnWidth = (prop: string) => {
    if (localColumnsConfig.value[prop]) {
        return localColumnsConfig.value[prop].width
    }
    if (props.columnsConfig[prop]) {
        return props.columnsConfig[prop].width
    }
    return DEFAULT_COLUMN_WIDTH
}
const resizeColumn = async (newWidth: number, oldWidth: number, { property }: { property: string }) => {
    // Skip in RTL mode - we use custom resize handler
    if (isRTLMode.value) {
        return
    }
    // This handler is only used in LTR mode (Element UI's native resize)
    if (Math.abs(newWidth - oldWidth) <= 2) {
        return
    }
    if (!localColumnsConfig.value) {
        localColumnsConfig.value = {}
    }
    localColumnsConfig.value[property].width = Math.ceil(newWidth)
    emitColumnsConfigUpdate()
}

// ============================================
// RTL Column Resize Implementation
// ============================================
// In RTL mode, resize handle is on the LEFT edge of columns
// Drag LEFT = expand, Drag RIGHT = shrink

const RESIZE_ZONE = 20 // pixels from left edge to trigger resize
const DRAG_THRESHOLD = 3 // pixels of movement before drag starts

interface RTLResizeState {
    columnProp: string
    startX: number
    startWidth: number
    th: HTMLTableCellElement
    didDrag: boolean
}

interface ColumnAtPosition {
    th: HTMLTableCellElement
    prop: string
    nearLeftEdge: boolean
    rect: DOMRect
}

// Simple state - null when not resizing
let resizeState: RTLResizeState | null = null

// Flag to ignore sort events right after resize
let ignoreSortUntil = 0

let rtlCleanup: (() => void) | null = null

const setupRTLResizeListeners = () => {
    if (rtlCleanup) rtlCleanup()
    if (!isRTLMode.value || !props.withColumnsManage || !tableElementRef.value) return

    const table = tableElementRef.value
    const headerWrapper = table.querySelector<HTMLElement>('.el-table__header-wrapper')
    if (!headerWrapper) return

    // Helper: find column and check if mouse is near left edge
    const getColumnAtPosition = (e: MouseEvent): ColumnAtPosition | null => {
        const target = e.target as Element
        const th = target.closest<HTMLTableCellElement>('th.el-table__cell')
        if (!th) return null
        const prop = th.querySelector('[data-prop]')?.getAttribute('data-prop')
        if (!prop) return null
        const rect = th.getBoundingClientRect()
        const nearLeftEdge = (e.clientX - rect.left) <= RESIZE_ZONE
        return {
            th,
            prop,
            nearLeftEdge,
            rect
        }
    }

    // Update column width via reactive state (Vue handles the re-render)
    const setColumnWidth = (width: number): void => {
        if (!resizeState) return
        const { columnProp } = resizeState

        if (localColumnsConfig.value[columnProp]) {
            localColumnsConfig.value[columnProp].width = Math.round(width)
        }
    }

    // MOUSEDOWN: Start resize if near left edge
    const onMouseDown = (e: MouseEvent): void => {
        const col = getColumnAtPosition(e)
        if (!col?.nearLeftEdge) return
        if (!localColumnsConfig.value[col.prop]) return

        e.preventDefault()
        e.stopPropagation()

        resizeState = {
            columnProp: col.prop,
            startX: e.clientX,
            startWidth: Number(localColumnsConfig.value[col.prop].width) || DEFAULT_COLUMN_WIDTH,
            th: col.th,
            didDrag: false
        }

        col.th.classList.add('rtl-resizing')
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'

        document.addEventListener('mousemove', onDocMouseMove)
        document.addEventListener('mouseup', onDocMouseUp)
    }

    // MOUSEMOVE on document: Update width during drag
    const onDocMouseMove = (e: MouseEvent): void => {
        if (!resizeState) return

        const delta = e.clientX - resizeState.startX

        // Only start actual resizing after threshold
        if (!resizeState.didDrag && Math.abs(delta) < DRAG_THRESHOLD) return

        if (!resizeState.didDrag) {
            resizeState.didDrag = true
        }

        // RTL: drag right (positive delta) = shrink, drag left (negative delta) = expand
        const newWidth = Math.max(50, resizeState.startWidth - delta)
        setColumnWidth(newWidth)
    }

    // Block click events after resize to prevent Element UI sort
    const blockNextClick = (e: MouseEvent): void => {
        e.stopPropagation()
        e.stopImmediatePropagation()
        e.preventDefault()
    }

    // Capture options for event listeners
    const captureOptions: AddEventListenerOptions = {
        capture: true,
        once: true
    }

    // MOUSEUP on document: Finish resize
    const onDocMouseUp = (e: MouseEvent): void => {
        document.removeEventListener('mousemove', onDocMouseMove)
        document.removeEventListener('mouseup', onDocMouseUp)

        if (!resizeState) return

        const { columnProp, startX, startWidth, th, didDrag } = resizeState

        // Clean up visual state
        th.classList.remove('rtl-resizing')
        document.body.style.cursor = ''
        document.body.style.userSelect = ''

        // If we actually dragged, save the new width
        if (didDrag) {
            // Set ignore flag
            ignoreSortUntil = Date.now() + 500

            // Block the next click event on the header to prevent Element UI sort
            // Use capture phase to intercept before Element UI sees it
            th.addEventListener('click', blockNextClick, captureOptions)

            const delta = e.clientX - startX
            const finalWidth = Math.max(50, startWidth - delta)

            if (localColumnsConfig.value[columnProp]) {
                localColumnsConfig.value[columnProp].width = Math.round(finalWidth)
            }
            emitColumnsConfigUpdate()

            // Clean up the click blocker after a short delay if it wasn't triggered
            setTimeout(() => {
                th.removeEventListener('click', blockNextClick, { capture: true })
            }, 100)
        }

        resizeState = null
    }

    // MOUSEMOVE on header: Show resize cursor when near left edge
    const onHeaderMouseMove = (e: MouseEvent): void => {
        if (resizeState) return // Don't change cursor during resize

        // Clear previous hover states
        headerWrapper.querySelectorAll<HTMLTableCellElement>('th.el-table__cell').forEach(th => {
            th.style.cursor = ''
            th.classList.remove('rtl-resize-hover')
        })

        const col = getColumnAtPosition(e)
        if (col?.nearLeftEdge) {
            col.th.style.cursor = 'col-resize'
            col.th.classList.add('rtl-resize-hover')
        }
    }

    // MOUSELEAVE on header: Clear hover state
    const onHeaderMouseLeave = (): void => {
        if (resizeState) return
        headerWrapper.querySelectorAll<HTMLTableCellElement>('th.el-table__cell').forEach(th => {
            th.style.cursor = ''
            th.classList.remove('rtl-resize-hover')
        })
    }

    // Attach listeners
    headerWrapper.addEventListener('mousedown', onMouseDown, true)
    headerWrapper.addEventListener('mousemove', onHeaderMouseMove)
    headerWrapper.addEventListener('mouseleave', onHeaderMouseLeave)

    rtlCleanup = () => {
        headerWrapper.removeEventListener('mousedown', onMouseDown, true)
        headerWrapper.removeEventListener('mousemove', onHeaderMouseMove)
        headerWrapper.removeEventListener('mouseleave', onHeaderMouseLeave)
        document.removeEventListener('mousemove', onDocMouseMove)
        document.removeEventListener('mouseup', onDocMouseUp)
    }
}

const onSortChange = ({ order, prop }: { order: string, prop: string }) => {
    // Ignore sort events triggered by RTL resize mouseup
    if (Date.now() < ignoreSortUntil) {
        return
    }

    const layout = props.widgetData.WidgetLayout || {}
    const updatedWidget: WidgetExtended = {
        ...props.widgetData,
        WidgetLayout: {
            ...layout,
            sortingParams: {
                ...(layout.sortingParams || {}),
                order,
                prop,
            },
        },
    }
    emit('update-widget', updatedWidget)
}
const updateColumnsConfig = (config: ColumnConfigType) => {
    localColumnsConfig.value = config
}
const updateColumnConfig = (columnProp: string, columnConfig: ColumnConfig) => {
    localColumnsConfig.value[columnProp] = columnConfig
    if (tableHeadersConfig.value && Object.keys(tableHeadersConfig.value).length) {
        emitColumnsAndHeadersConfigUpdate()
    } else {
        emitColumnsConfigUpdate()
    }
}
const updateGlobalHeadersConfig = (config: TableHeadersConfig) => {
    tableHeadersConfig.value = config
}
const handleColumnFilterChange = (columnProp: string, values: Array<string | number>) => {
    if (!localColumnsConfig.value[columnProp]) {
        localColumnsConfig.value[columnProp] = {
            ...defaultColumnConfig,
            order: 0
        }
    }

    if (values.length === 0) {
        delete localColumnsConfig.value[columnProp].filterValues
    } else {
        localColumnsConfig.value[columnProp].filterValues = values
    }

    if (tableHeadersConfig.value && Object.keys(tableHeadersConfig.value).length) {
        emitColumnsAndHeadersConfigUpdate()
    } else {
        emitColumnsConfigUpdate()
    }
}

const syncColumnsLabelTypeConfig = (labelType: string) => {
    const opts = getTableHeaderGlobalOptions()
    const isShortText = labelType === opts.shortText.label
    const isFullText = labelType === opts.fullText.label
    const isCustom = labelType === opts.custom.label

    if (!isShortText && !isFullText && !isCustom) return

    Object.keys(localColumnsConfig.value).forEach((prop) => {
        const col = localColumnsConfig.value[prop]
        if (!col) return

        if (isCustom) {
            localColumnsConfig.value[prop] = {
                ...col,
                showShortTextLabel: false,
                showFullTextLabel: false
            }
            return
        }

        localColumnsConfig.value[prop] = {
            ...col,
            showShortTextLabel: isShortText,
            showFullTextLabel: isFullText
        }
    })
}

const onApply = () => {
    emitColumnsAndHeadersConfigUpdate()
    isManageSideModalOpen.value = false
    setInColumnsStatusValue(false)
}
const cancelManage = () => {
    if (manageColumnsInitialState.value) {
        localColumnsConfig.value = cloneDeep(manageColumnsInitialState.value.columnsConfig)
        tableHeadersConfig.value = cloneDeep(manageColumnsInitialState.value.tableHeadersConfig)
    }
    isManageSideModalOpen.value = false
    setInColumnsStatusValue(false)
}

const changeIconVisibility = (val: boolean) => {
    tableHeadersConfig.value = {
        ...tableHeadersConfig.value,
        showAllIcons: val
    }
}
const changeLabelType = (val: string) => {
    syncColumnsLabelTypeConfig(val)
    tableHeadersConfig.value = {
        ...tableHeadersConfig.value,
        labelTextType: val
    }
}
const generateColumnsConfig = async (options: { autoLabelMode?: boolean } = {}) => {
    if (!props.widgetData) return
    localColumnsConfig.value = await getColumnsConfig(sortedColumns.value, localColumnsConfig.value, options)
    emitColumnsConfigUpdate()
}
const generateColumnsConfigAutomatically = () => {
    generateColumnsConfig({ autoLabelMode: true })
}

//the function that generate the hidden and visible columns config for a new table depends on old table component manage before redesign
const generateOldVisibleColumnsConfig = () => {
    const config: ColumnsObjectConfig = {}
    props.columns.forEach((column, i) => {
        config[column.prop] = {
            ...defaultColumnConfig,
            hidden: !oldVersionVisibleColumns.value.includes(column.prop),
            order: i,
        }
    })
    return config
}

const generateDefaultAvailableColumnsConfig = (defaultAvailableColumns: Record<string, boolean>) => {
    const config: ColumnsObjectConfig = {}
    props.columns.forEach((column, i) => {
        config[column.prop] = {
            ...defaultColumnConfig,
            hidden: !defaultAvailableColumns[column.prop],
            order: i,
        }
    })
    return config
}

/**
 * Ensures all columns from props.columns have corresponding config entries.
 * This handles the case where new columns are added to a template after a widget was created.
 */
const ensureAllColumnsHaveConfig = () => {
    let hasNewColumns = false
    const currentMaxOrder = Object.values(localColumnsConfig.value).reduce(
        (max, config) => Math.max(max, config.order ?? 0),
        -1
    )

    props.columns.forEach((column, index) => {
        if (!localColumnsConfig.value[column.prop]) {
            hasNewColumns = true
            localColumnsConfig.value[column.prop] = {
                ...defaultColumnConfig,
                order: currentMaxOrder + index + 1
            }
        }
    })

    return hasNewColumns
}
const updateTableHeight = () => {
    const widgetElement = document.getElementById(`widgetId-${props.widgetData.WidgetID}`)
    if (widgetElement && widgetElement.clientHeight) {
        tableHeight.value = widgetElement.clientHeight
    }
    if (props.paginated) {
        tableHeight.value = tableHeight.value - props.paginationHeight
    }
}

function openExportDataModal () {
    const _visibleColumns = [ ...sortedColumns.value ]
    const _hiddenColumns = [ ...columnByVisibility.value.hidden ]
    exportDataModalRef.value?.open(_visibleColumns, _hiddenColumns)
}

onMounted(() => {
    if (isColumnsConfigAdded.value) return

    if (oldVersionVisibleColumns.value.length) {
        localColumnsConfig.value = generateOldVisibleColumnsConfig()
        generateColumnsConfig()
        return
    }
    if (props.defaultAvailableColumns) {
        localColumnsConfig.value = generateDefaultAvailableColumnsConfig(props.defaultAvailableColumns)
    }
    generateColumnsConfig()
    isMounted.value = true
})

//Watch
watch(() => props.widgetData.WidgetLayout, (val) => {
    updateTableHeight()
    if (val.tableHeadersConfig && !isManageSideModalOpen.value) {
        tableHeadersConfig.value = val.tableHeadersConfig
    }
}, {
    immediate: true,
    deep: true
})

watch(() => isManageSideModalOpen.value, (isOpen) => {
    if (!isOpen) return
    manageColumnsInitialState.value = {
        columnsConfig: cloneDeep(localColumnsConfig.value),
        tableHeadersConfig: cloneDeep(tableHeadersConfig.value)
    }
})

watch(() => props.columnsConfig, (val) => {
    // Skip updating localColumnsConfig during RTL resize to prevent snap-back
    if (resizeState !== null) {
        return
    }

    if ((!val || !Object.values(val).length) && isMounted.value) {
        generateColumnsConfig()
    }
    localColumnsConfig.value = cloneDeep(val)
    // Ensure all columns have config entries (handles columns added to template after widget creation)
    ensureAllColumnsHaveConfig()
}, {
    immediate: true,
    deep: true
})

// Watch for changes to columns - needed when columns load async or change dynamically (e.g., queue table view mode)
watch(() => props.columns, () => {
    if (Object.keys(localColumnsConfig.value).length) {
        ensureAllColumnsHaveConfig()
    }
}, {
    deep: true
})

// Re-setup RTL resize listeners when RTL mode changes
watch(isRTLMode, () => {
    if (tableElementRef.value) {
        setupRTLResizeListeners()
    }
})

// Recalculate table layout when wrap text mode or display queues as row changes
watch([ isWrapText, sortedColumns ], async () => {
    if (tableRef.value) {
        await nextTick()
        tableRef.value.doLayout()
    }
})

// Clear sort in ElTable when order changes from non-null to null
watch(
    () => sortingParams.value?.order,
    (newOrder, oldOrder) => {
        if (!newOrder && oldOrder) {
            nextTick(() => {
                tableRef.value?.clearSort()
            })
        }
    }
)

onBeforeUnmount(() => {
    cleanUp()
    if (rtlCleanup) rtlCleanup()
})
</script>

<style lang="scss">
.redesign-table-component {

    @keyframes color_bg_warning_callers {
        0% {
            background-color: transparent;
        }
        50% {
            background-color: var(--maximum-warning-callers-color);
        }
        100% {
            background-color: transparent;
        }
    }

    @keyframes color_bg_limit_callers {
        0% {
            background-color: transparent;
        }
        50% {
            background-color: var(--maximum-limit-callers-color);
        }
        100% {
            background-color: transparent;
        }
    }

    @keyframes color_bg_warning_maxRing {
        0% {
            background-color: transparent;
        }
        50% {
            background-color: var(--maximum-warning-max-ring-time-color);
        }
        100% {
            background-color: transparent;
        }
    }

    @keyframes color_bg_limit_maxRing {
        0% {
            background-color: transparent;
        }
        50% {
            background-color: var(--maximum-limit-max-ring-time-color);
        }
        100% {
            background-color: transparent;
        }
    }

    .el-table .cell {
        white-space: nowrap !important;
    }

    &.table-wrap-text {
        .el-table th.el-table__cell .cell .table-header-label, .el-table td.el-table__cell .cell {
            white-space: normal !important;
            overflow-wrap: break-word;
            word-break: break-word;
            max-width: 100% !important;
        }
    }

    .el-table--striped .el-table__body tr.el-table__row--striped td.el-table__cell {
        background-color: color-mix(in srgb, var(--mainBgColor-color) 90%, #BEC2C9);
    }

    .el-table--striped .el-table__body tr.el-table__row--striped:hover > td.el-table__cell {
        background-color: var(--el-table-row-hover-bg-color);
    }

    tr.el-table__row--striped td.el-table__cell:has(.is--callers-status-warning),
    tr.el-table__row td.el-table__cell:has(.is--callers-status-warning) {
        animation: color_bg_warning_callers 3s infinite !important;
    }

    tr.el-table__row td.el-table__cell:has(.is--callers-status-limit),
    tr.el-table__row--striped td.el-table__cell:has(.is--callers-status-limit) {
        animation: color_bg_limit_callers 3s infinite !important;
    }

    tr.el-table__row--striped td.el-table__cell:has(.is--waiting-status-warning),
    tr.el-table__row td.el-table__cell:has(.is--waiting-status-warning) {
        animation: color_bg_warning_maxRing 3s infinite !important;
    }

    tr.el-table__row--striped td.el-table__cell:has(.is--waiting-status-limit),
    tr.el-table__row td.el-table__cell:has(.is--waiting-status-limit) {
        animation: color_bg_limit_maxRing 3s infinite !important;
    }

    .el-table th.el-table__cell > .cell {
        display: flex !important;
        width: auto !important;
        max-width: max-content !important;
    }

    .el-table td.el-table__cell .cell {
        display: flex !important;
        width: max-content !important;
        max-width: max-content !important;
    }

    // Only when first column is sticky: constrain cell so text clips instead of overflowing neighbors
    &.fix-first-column {
        .el-table td.el-table-fixed-column--left.el-table__cell > .cell {
            width: 100% !important;
            max-width: 100% !important;
            overflow: hidden !important;
        }
    }

    .el-table th.el-table__cell {

            & > .cell > .caret-wrapper {
                position: absolute;
                right: 5px;
                top: 0;
                bottom: 0;
                margin: auto;
                max-width: 12px;
            }

        &.is-left > .cell {
            margin: 0 auto 0 0;
        }

        &.is-center > .cell {
            margin: 0 auto 0 auto;
        }

        &.is-right > .cell {
            margin: 0 0 0 auto;
        }
    }

    &.is-rtl .el-table th.el-table__cell {
        & > .cell > .caret-wrapper {
                left: 5px;
                right: auto;
            }
    }

    .el-table td.el-table__cell {
        &.is-left > .cell {
            justify-content: flex-start !important;

            & > .cell-wrapper {
                justify-content: flex-start !important;
            }
        }

        &.is-center > .cell {
            width: 100% !important;
            max-width: 100% !important;
            justify-content: center !important;

            & > .cell-wrapper {
                justify-content: center !important;
            }
        }

        &.is-right > .cell {
            width: 100% !important;
            max-width: 100% !important;
            justify-content: flex-end !important;

            & > .cell-wrapper {
                justify-content: flex-end !important;
            }
        }
    }

    // RTL mode: invert justify-content for left/right alignment
    &.is-rtl .el-table td.el-table__cell {
        &.is-left > .cell {
            width: 100% !important;
            max-width: 100% !important;
            justify-content: flex-end !important;
            text-align: left !important;

            > div {
                width: auto !important;
            }

            & > .cell-wrapper {
                justify-content: flex-end !important;
            }
        }

        &.is-right > .cell {
            width: 100% !important;
            max-width: 100% !important;
            justify-content: flex-start !important;

            & > .cell-wrapper {
                justify-content: flex-start !important;
            }
        }
    }

    .no-horizontal-borders .el-table__row,
    .no-horizontal-borders .el-table__header th {
        border-bottom: none;
    }

    .el-table td.el-table__cell {
        border-bottom: none;
        padding-top: var(--table-cell-vertical-padding);
        padding-bottom: var(--table-cell-vertical-padding);
    }

    .redesign-column {
        color: var(--mainTextColor-color) !important;
    }

    .rendered {
        .el-table td.el-table__cell .cell {
            width: 100%;
        }
    }

    .el-table td .cell,
    .el-table th .cell {
        color: black;
        font-size: var(--dynamic-font-size);
        line-height: 1.4;
    }
}
</style>

